import { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Space,
  Drawer,
  message,
  Upload,
  Grid,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  useCreateLocation,
  useFetchSingleLocation,
} from '../../Pages/locations/Hooks';
import { useFilUpload } from '../../Hooks/Index';
import { BsPlus } from 'react-icons/bs';
import { validations } from '../../utils/validation';
import PropTypes from 'prop-types';
import { Typography } from 'antd';
import { countryCode } from '../../utils/utils';
const { Title } = Typography;
const { useBreakpoint } = Grid;

const DUMMY_BUSINESS_NAME = 'N/A';
const DUMMY_AREA = 'N/A';

const buildAddress = (values) => ({
  area: DUMMY_AREA,
  city: null,
  state: null,
  country: null,
  postal: values.address?.postal || null,
  additionalInfo: values.address?.additionalInfo || null,
});

const buildContactPerson = (values) => ({
  contactPersonFname: values.contactPersonFname,
  contactPersonLname: values.contactPersonLname,
  contactPersonPhoneNumber: '',
});

const buildPayload = ({
  operatorId,
  values,
  fileList,
  isEditing,
}) => {
  const address = buildAddress(values);
  const contactPerson = buildContactPerson(values);

  const payload = {
    operatorId: operatorId,
    locationName: values.locationName
      ? values.locationName.charAt(0).toUpperCase() + values.locationName.slice(1)
      : '',
    address,
    ...contactPerson,
    countryCode: values.countryCode || countryCode || '',
    locationFacilities: [],
    perUnitCommissions: values.isBillable ? (values.perUnitCommissions || 0) : 0,
    isBillable: values.isBillable || false,
    image: fileList[0]?.url || '',
  };

  if (!isEditing) {
    payload.businessName = DUMMY_BUSINESS_NAME;
  }

  return payload;
};

const LocationFormModal = ({ isOpen, onClose, editLocation }) => {
  const { t } = useTranslation('location');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { operatorId } = useOperatorStore();
  const [fileList, setFileList] = useState([]);
  const { data, isLoading } = useFetchSingleLocation({
    operatorId: operatorId,
    id: editLocation?._id,
  });

  const isEditing = !!editLocation;

  const { mutateAsync: uploadFile } = useFilUpload(); //

  const { mutate: createLocation, isPending } = useCreateLocation(
    { id: editLocation?._id },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['listStations']);
        form.resetFields();
        setFileList([]);
        onClose();
        message.success(
          t(
            editLocation?._id
              ? 'messages.locationUpdated'
              : 'messages.locationCreated'
          )
        );
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.message ||
          error?.response?.data?.status_message
        );
      },
    }
  );

  const handleFileUpload = async ({ file }) => {
    await uploadFile(file, {
      onSuccess: (result) => {
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: 'done',
            url: result.Location,
          },
        ]);

        message.success(t('messages.fileUploadSuccess', { fileName: file.name }));
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.message ||
          error?.response?.data?.status_message
        );
      },
    });
  };

  useEffect(() => {
    if (isEditing && data?.[0]?.image) {
      setFileList([
        {
          uid: '-1',
          name: t('messages.existingImage'),
          status: 'done',
          url: data?.[0]?.image,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [isEditing, data, t]);

  const onFinish = (values) => {
    const payload = buildPayload({
      operatorId,
      values,
      fileList,
      isEditing,
    });
    createLocation(payload);
  };

  return (
    <Drawer
      maskClosable={false}
      title={isEditing ? <Title level={4} className="!mb-0">{t('modal.edit')}</Title> : <Title level={4} className="!mb-0">{t('modal.title')}</Title>}
      placement="right"
      open={isOpen}
      width={isMobile ? '100%' : 600}
      loading={isLoading}
      onClose={onClose}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => form.submit()}
          >
            {t('modal.submit')}
          </Button>
          <Button onClick={onClose}>{t('modal.CancelButton')}</Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          locationName: data?.[0]?.locationName || '',
          contactPersonFname: data?.[0]?.contactPersonFname || '',
          contactPersonLname: data?.[0]?.contactPersonLname || '',
          countryCode: data?.[0]?.countryCode || import.meta.env.VITE_CURRENCY === 'INR' ? '91' : '90',
          perUnitCommissions: data?.[0]?.perUnitCommissions || 0,
          isBillable: data?.[0]?.isBillable || false,
          address: {
            postal: data?.[0]?.address?.postal || '',
            additionalInfo: data?.[0]?.address?.additionalInfo || '',
          },
        }}
      >

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="locationName"
              label={t('form.locationName.label')}
              rules={[
                ...(isEditing ? [] : [
                  validations.required(
                    t('form.locationName.validation.required')
                  )
                ]),
                validations.maxLengthWithTrim(
                  30,
                  t('form.locationName.validation.max')
                ),
                validations.noSpaces(t('form.Validation.RFIDNoSpaces')),
              ]}
              tooltip={t('form.locationName.tooltip')}
            >
              <Input
                placeholder={t('form.locationName.placeholder')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contactPersonFname"
              label={t('form.contact.firstName.label')}
              rules={[
                ...(isEditing ? [] : [
                  validations.required(
                    t('form.contact.firstName.validation')
                  )
                ]),
                validations.maxLengthWithTrim(
                  50,
                  t('form.Validation.Max50Characters')
                ),
                validations.noSpaces(t('form.Validation.RFIDNoSpaces')),
              ]}
              tooltip={t('form.contact.firstName.tooltip')}
            >
              <Input
                placeholder={t('form.contact.firstName.placeholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="contactPersonLname"
              label={t('form.contact.lastName.label')}
              rules={[
                ...(isEditing ? [] : [
                  validations.required(t('form.contact.lastName.validation'))
                ]),
                validations.maxLengthWithTrim(
                  50,
                  t('form.Validation.Max50Characters')
                ),
                validations.noSpaces(t('form.Validation.RFIDNoSpaces')),
              ]}
              tooltip={t('form.contact.lastName.tooltip')}
            >
              <Input placeholder={t('form.contact.lastName.placeholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label={t('messages.uploadImage')}>
              <Upload
                listType="picture-card"
                customRequest={handleFileUpload}
                fileList={fileList}
                showUploadList
                accept=".png,.jpg,.jpeg"
                onRemove={() => setFileList([])}
                onChange={({ fileList }) => setFileList(fileList.slice(-1))} // Ensures only 1 file
              >
                {fileList.length < 1 && (
                  <button
                    type="button"
                    className="flex gap-2 flex-col items-center"
                  >
                    <BsPlus size={20} /> {t('messages.upload')}
                  </button>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        {/* <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="isBillable"
              valuePropName="checked"
              label={t('form.isBillable.label')}
            >
              <Checkbox disabled={editLocation}>
                {t('form.isBillable.label')}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {isBillable && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="perUnitCommissions"
                label={t('form.perUnitCommissions.label')}
                rules={[
                  validations.required(
                    t('form.perUnitCommissions.validation.required')
                  ),
                  validations.validNumber(
                    t('form.perUnitCommissions.validation.invalid')
                  ),
                  validations.minValue(
                    0.1,
                    t('form.perUnitCommissions.validation.min') // Minimum value is 0.1%
                  ),
                  validations.maxValue(
                    100,
                    t('form.perUnitCommissions.validation.max') // Maximum value is 100%
                  ),
                ]}
              >
                <Input
                  type="number"
                  placeholder={t('form.perUnitCommissions.placeholder')}
                  min={0.1}
                  max={100}
                  step={0.1}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>
        )} */}

      </Form>
    </Drawer>
  );
};

LocationFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editLocation: PropTypes.shape({
    _id: PropTypes.string,
    locationName: PropTypes.string,
    businessName: PropTypes.string,
    locationOwnerId: PropTypes.string,
    contactPersonFname: PropTypes.string,
    contactPersonLname: PropTypes.string,
    image: PropTypes.string,
  }),
};

export default LocationFormModal;
